/**
 * Comparação de telefones entre fontes que escrevem o número de formas
 * diferentes (SA-1304).
 *
 * O cartão de contato compartilhado carrega o telefone como o remetente
 * digitou ("+55 (16) 98249-0044"); o banco guarda a forma canônica só com
 * dígitos; e números brasileiros antigos foram salvos sem o 9º dígito.
 * Comparar as strings cruamente erra nos três casos.
 */

const MIN_COMPARABLE_DIGITS = 8;
const LOCAL_DIGITS = 8;

function onlyDigits(phone: string | null | undefined): string {
  return (phone ?? "").replace(/\D/g, "");
}

/**
 * Trecho estável para buscar o número no backend: os 8 dígitos finais, os
 * únicos que sobrevivem a máscara, DDI omitido e 9º dígito. O filtro traz
 * candidatos; quem decide se é o mesmo número é `samePhoneNumber`.
 */
export function phoneSearchNeedle(
  phone: string | null | undefined
): string | null {
  const digits = onlyDigits(phone);
  return digits.length >= MIN_COMPARABLE_DIGITS
    ? digits.slice(-LOCAL_DIGITS)
    : null;
}

/**
 * Prefixo (DDI + DDD) sem o 9º dígito móvel, que aparece de um lado só quando
 * o número foi cadastrado antes da mudança da Anatel.
 */
function comparablePrefix(digits: string): string {
  const prefix = digits.slice(0, -LOCAL_DIGITS);
  return prefix.endsWith("9") ? prefix.slice(0, -1) : prefix;
}

export function samePhoneNumber(
  a: string | null | undefined,
  b: string | null | undefined
): boolean {
  const digitsA = onlyDigits(a);
  const digitsB = onlyDigits(b);

  if (
    digitsA.length < MIN_COMPARABLE_DIGITS ||
    digitsB.length < MIN_COMPARABLE_DIGITS
  ) {
    return false;
  }

  if (digitsA.slice(-LOCAL_DIGITS) !== digitsB.slice(-LOCAL_DIGITS)) {
    return false;
  }

  const prefixA = comparablePrefix(digitsA);
  const prefixB = comparablePrefix(digitsB);

  // Sem DDD dos dois lados não dá para afirmar que é a mesma pessoa: 8 dígitos
  // se repetem entre DDDs. Preferimos não casar a abrir a conversa errada.
  if (!prefixA || !prefixB) return prefixA === prefixB;

  // Um lado pode vir sem DDI ("16 98249-0044" vs "5516982490044"): basta que
  // um prefixo termine no outro.
  return prefixA.endsWith(prefixB) || prefixB.endsWith(prefixA);
}
